import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, AppleLogo, AndroidLogo, ShareFat, Plus, DotsThreeVertical, DownloadSimple } from "@phosphor-icons/react";

const APPLE_ICONS = [AppleLogo, ShareFat, Plus, DownloadSimple];
const ANDROID_ICONS = [AndroidLogo, DotsThreeVertical, Plus, DownloadSimple];

function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return null;
}

export default function InstallGuide() {
  const { t } = useTranslation();
  const appUrl = window.location.origin;
  const detected = useMemo(detectPlatform, []);
  const [activeTab, setActiveTab] = useState(detected || "ios");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(appUrl);
      toast.success(t("install.copiedSuccess"));
    } catch (e) {
      toast.error(t("install.copyFailedError"));
    }
  };

  const icons = activeTab === "ios" ? APPLE_ICONS : ANDROID_ICONS;
  const stepTexts = t(activeTab === "ios" ? "install.iosSteps" : "install.androidSteps", { returnObjects: true });
  const steps = stepTexts.map((text, idx) => ({ text, icon: icons[idx] }));

  return (
    <div className="min-h-screen bg-background grain-overlay px-6 py-10 md:px-16" data-testid="install-guide-page">
      <p className="font-mono-tag text-xs text-primary mb-2">{t("install.tagline")}</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-3">{t("install.title")}</h1>
      <p className="text-muted-foreground max-w-lg mb-10">{t("install.description")}</p>

      <div className="grid md:grid-cols-[auto_1fr] gap-10 max-w-3xl">
        <div className="border-2 border-foreground brutal-shadow bg-white p-4 h-fit flex flex-col items-center gap-3" data-testid="install-qr-code">
          <QRCodeSVG value={appUrl} size={168} fgColor="#181311" bgColor="#ffffff" marginSize={2} />
          <p className="font-mono-tag text-[10px] text-muted-foreground">{t("install.scanHint")}</p>
        </div>

        <div className="space-y-6 min-w-0">
          <div className="flex gap-2 border border-border bg-card p-1 w-fit">
            <button
              data-testid="install-tab-ios"
              onClick={() => setActiveTab("ios")}
              className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-colors ${activeTab === "ios" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            >
              <AppleLogo size={16} /> iPhone
              {detected === "ios" && <span className="font-mono-tag text-[9px] opacity-80">{t("install.yourDevice")}</span>}
            </button>
            <button
              data-testid="install-tab-android"
              onClick={() => setActiveTab("android")}
              className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-colors ${activeTab === "android" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
            >
              <AndroidLogo size={16} /> Android
              {detected === "android" && <span className="font-mono-tag text-[9px] opacity-80">{t("install.yourDevice")}</span>}
            </button>
          </div>

          <ol className="space-y-3" data-testid="install-steps-list">
            {steps.map((step, idx) => (
              <li key={idx} className="flex items-start gap-3 border border-border bg-card p-4">
                <span className="font-display text-lg font-black text-primary shrink-0 w-6">{idx + 1}</span>
                <step.icon size={20} className="shrink-0 mt-0.5 text-muted-foreground" />
                <p className="text-sm leading-relaxed">{step.text}</p>
              </li>
            ))}
          </ol>

          <div className="flex gap-2">
            <input readOnly value={appUrl} onFocus={(e) => e.target.select()} data-testid="install-link-input" className="flex-1 min-w-0 border border-border bg-card px-3 py-2 text-sm rounded-none" />
            <button data-testid="install-copy-link-button" onClick={handleCopy} className="shrink-0 px-4 py-2 bg-primary text-primary-foreground font-semibold text-sm flex items-center gap-2 hover:bg-primary/90 transition-colors">
              <Copy size={16} weight="bold" /> {t("install.copyLink")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
